import { EDUCATOR_ROLES } from "@/features/router/roles";
import { useAuth } from "@/features/auth";
import ResourceSection from "./components/ResourceSection";
import SelfCareChallengeSection from "./components/SelfCareChallengeSection";
import BrainModelSection from "./components/BrainModelSection";
import LearnMore from "./components/LearnMore";
import {
  EDUCATOR_BRAIN_SECTION,
  EDUCATOR_FOOTER_NOTE,
  EDUCATOR_HERO,
  EDUCATOR_SECTIONS,
  SELF_CARE_CHALLENGE_SECTION,
  STUDENT_BRAIN_SECTION,
  STUDENT_FOOTER_NOTE,
  STUDENT_HERO,
  STUDENT_SECTIONS,
} from "./resourcesData";

const HEADER_WIDTH = "mx-auto w-full px-16";
const CONTENT_WIDTH = "mx-auto w-full max-w-screen-2xl";

export default function ResourcesPage() {
  const { user } = useAuth();

  // `user` is null for a beat before hydration; without this guard a teacher
  // briefly sees the student view.
  const hasUser = Boolean(user?.role);
  const isEducator = EDUCATOR_ROLES.includes(user?.role);

  const hero = isEducator ? EDUCATOR_HERO : STUDENT_HERO;
  const sections = isEducator ? EDUCATOR_SECTIONS : STUDENT_SECTIONS;
  const brainSection = isEducator
    ? EDUCATOR_BRAIN_SECTION
    : STUDENT_BRAIN_SECTION;
  const footerNote = isEducator ? EDUCATOR_FOOTER_NOTE : STUDENT_FOOTER_NOTE;
  // Unqualified until the role is known, so a teacher never sees "Student".
  const title = hasUser
    ? `${isEducator ? "Educator" : "Student"} Resources`
    : "Resources";

  return (
    <div className="tw">
      <div className="mt-16 flex min-h-screen flex-col border border-[#DDDDDD] bg-[#F4F4F3] sm:ml-[130px] sm:mt-0">
        <div
          className="sticky top-0 z-10 w-full bg-white px-3 py-3 shadow-md sm:px-8 sm:py-4"
          data-onboarding-id="resources-top"
        >
          <div className={`flex flex-col gap-0.5 text-center ${HEADER_WIDTH}`}>
            <h1 className="m-0 text-xl font-semibold sm:text-2xl">{title}</h1>
            <p className="m-0 text-sm sm:text-lg text-gray-600">
              {isEducator
                ? "Videos, lesson plans and tools to support you and your students."
                : "Videos and workbooks to help you build your wellness."}
            </p>
          </div>
        </div>

        {hasUser && (
          <div className="flex-1 px-3 pb-16 pt-4 sm:px-8 sm:pb-10">
            <div className={CONTENT_WIDTH}>
              <div className="w-full rounded-xl border border-[#BFE3D3] bg-[#F2FBF7] p-6 sm:p-8">
                <h2 className="m-0 text-base font-semibold text-[#0f5a41] sm:text-lg">
                  {hero.heading}
                </h2>
                <p className="m-0 mt-2 text-sm text-[#1E2D3D] sm:text-base">
                  {hero.body}
                </p>
                {hero.learnMore && (
                  <div className="mt-3">
                    <LearnMore {...hero.learnMore} />
                  </div>
                )}
              </div>

              {sections.map((section) => (
                <ResourceSection key={section.id} section={section} />
              ))}

              {isEducator && (
                <SelfCareChallengeSection
                  section={SELF_CARE_CHALLENGE_SECTION}
                />
              )}

              <BrainModelSection section={brainSection} />

              <p className="m-0 mt-8 text-sm italic text-[#555555] sm:text-base">
                {footerNote}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
